import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, FileText, User } from "lucide-react";
import { initials, ageDisplay } from "@/lib/format";

/** New Consultation dialog — searchable patient picker (by name / mobile / email).
 *  On pick, quick-books a today/now appointment and navigates to the consult form. */
export default function NewConsultationDialog({ open, onOpenChange }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(null); // patient id being started

  useEffect(() => {
    if (!open) { setQ(""); setPatients([]); setStarting(null); return; }
    // Load initial page (first 10) so the user has something to start with.
    setLoading(true);
    api.get("/patients", { params: { page_size: 10 } })
      .then(({ data }) => setPatients(data.items || data || []))
      .catch(() => toast.error("Could not load patients"))
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      setLoading(true);
      api.get("/patients", { params: { q: q || undefined, page_size: 12 } })
        .then(({ data }) => setPatients(data.items || data || []))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  const startFor = async (p) => {
    setStarting(p.id);
    try {
      const { data } = await api.post("/appointments/quick", { patient_id: p.id });
      toast.success(`Consult started with ${p.name}`);
      onOpenChange(false);
      nav(`/consultation/new?patient=${p.id}&appt=${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start consultation");
    } finally { setStarting(null); }
  };

  const rows = useMemo(() => patients || [], [patients]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-[24px] max-w-xl w-[calc(100vw-2rem)] sm:w-full max-h-[85vh] p-4 sm:p-6 flex flex-col gap-0"
        data-testid="new-consult-dialog"
      >
        <DialogHeader className="flex-none">
          <DialogTitle className="text-lg sm:text-xl font-semibold tracking-tight text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>
            New consultation
          </DialogTitle>
          <DialogDescription className="text-xs text-[#8A9992]">
            Pick a patient by name, mobile, or email. We&rsquo;ll auto-book today&rsquo;s walk-in appointment and open the consult form.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A9992]" />
            <input
              data-testid="new-consult-search"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, mobile, or email"
              className="input-clean pl-10"
            />
          </div>
          <div className="text-[11px] text-[#8A9992] mt-1.5">
            We&rsquo;ll auto-book a today&rsquo;s appointment when you pick a patient.
          </div>
        </div>

        <div className="mt-4 flex-1 min-h-0 overflow-y-auto space-y-1.5 pr-1 -mr-1">
          {loading ? (
            <div className="text-xs text-[#8A9992] text-center py-6 inline-flex items-center gap-2 justify-center w-full">
              <Loader2 size={12} className="animate-spin" /> Searching…
            </div>
          ) : rows.length === 0 ? (
            <div className="text-xs text-[#8A9992] text-center py-8 bg-[#F7F4EC]/60 rounded-2xl border border-dashed border-[#E8E6E1]">
              {q ? `No patients match "${q}".` : "No patients found. Add one from the dashboard first."}
            </div>
          ) : rows.map((p) => (
            <button
              key={p.id}
              onClick={() => startFor(p)}
              disabled={!!starting}
              className="w-full flex items-center gap-3 rounded-2xl px-3 py-2.5 text-left row-hover disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              data-testid={`new-consult-patient-${p.id}`}
            >
              <div className="w-9 h-9 rounded-full bg-[#0A4C36]/8 text-[#0A4C36] flex items-center justify-center font-semibold text-xs flex-none">
                {initials(p.name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-[#1F2A24] truncate">{p.name}</div>
                <div className="text-[11px] text-[#8A9992] truncate">
                  {p.gender} · {ageDisplay(p)}{p.mobile ? ` · ${p.mobile}` : ""}{p.email ? ` · ${p.email}` : ""}
                </div>
              </div>
              <div className="flex-none inline-flex items-center gap-1 text-[#0A4C36] text-xs font-semibold">
                {starting === p.id ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
                {starting === p.id ? "Starting…" : "Start"}
              </div>
            </button>
          ))}
        </div>

        <div className="flex-none flex items-center justify-between gap-2 pt-3 border-t border-[#EFECE5] mt-3">
          <div className="text-[11px] text-[#8A9992] inline-flex items-center gap-1.5">
            <User size={11} /> {rows.length} patient{rows.length === 1 ? "" : "s"}
          </div>
          <button onClick={() => onOpenChange(false)} className="btn-outline-emerald text-xs" data-testid="new-consult-cancel">
            Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
